import { useState, useEffect, useRef } from "react";

import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";

import {
  BADGES,
  BADGE_CATEGORIES,
  getEarnedBadges,
  getLockedBadges,
  getDisplayBadges,
  getBadgeProgress,
} from "../components/achievements/badgeDefinitions";
import BadgeDetailModal from "../components/achievements/BadgeDetailModal";
import OctagonBadge from "../components/achievements/OctagonBadge";
import { useAuth } from "../contexts/AuthContext";
import apiService from "../services/api";
import { isReadOnlyPlaylistEntry } from "../utils/playlistDashboard";

// Animated user count component with pulse effect on change
const AnimatedUserCount = ({ count, onClick }) => {
  const [displayCount, setDisplayCount] = useState(count);
  const [_isAnimating, setIsAnimating] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const prevCountRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (prevCountRef.current === null) {
      prevCountRef.current = count;
      setDisplayCount(count);

      return;
    }

    if (count !== prevCountRef.current) {
      setIsAnimating(true);
      const duration = 300;
      const startCount = prevCountRef.current;
      const diff = count - startCount;
      const startTime = Date.now();

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeProgress = 1 - Math.pow(1 - progress, 3);

        setDisplayCount(Math.round(startCount + diff * easeProgress));

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          prevCountRef.current = count;
          setTimeout(() => setIsAnimating(false), 500);
        }
      };

      requestAnimationFrame(animate);
    }
  }, [count]);

  const showAddUser = !isMobile && isHovered;

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClick) onClick();
  };

  return (
    <div
      className={`flex items-center justify-center w-full sm:w-28 h-12 sm:h-28 flex-shrink-0 rounded-full shadow-lg border-2 sm:border-4 transition-all duration-200 select-none sm:aspect-square ${
        isHovered
          ? "bg-gradient-to-br from-purple-600 to-pink-600 scale-105 border-purple-400/50"
          : "bg-gradient-to-br from-gray-600 to-gray-800 dark:from-gray-700 dark:to-gray-900 border-gray-500/50"
      }`}
      style={{ cursor: "pointer" }}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className="flex sm:flex-col items-center justify-center gap-2 sm:gap-0 w-full h-full"
        style={{ pointerEvents: "none" }}
      >
        {showAddUser ? (
          <>
            <i className="fas fa-user-plus text-xl sm:text-3xl text-white" />
            <span className="text-sm sm:text-sm text-white/90 sm:mt-1 font-medium">
              Add User
            </span>
          </>
        ) : (
          <>
            <span className="text-2xl sm:text-3xl font-bold text-white">
              {displayCount}
            </span>
            <span className="text-xs sm:text-xs text-gray-300 uppercase tracking-wider">
              Users
            </span>
          </>
        )}
      </div>
    </div>
  );
};

// macOS Dock-style magnification badge
const DockBadge = ({
  badge,
  index,
  mouseX,
  onClick,
  isMobile = false,
  isNew = false,
  isFirstView = false,
}) => {
  const ref = useRef(null);
  const MAGNIFICATION = 1.6;
  const DISTANCE = 120;
  const MAX_EXTRA_MARGIN = 12;

  const distance = useTransform(mouseX, (val) => {
    if (isMobile) return Infinity;
    const bounds = ref.current?.getBoundingClientRect() ?? { x: 0, width: 0 };

    return val - bounds.x - bounds.width / 2;
  });

  const scaleSync = useTransform(
    distance,
    [-DISTANCE, 0, DISTANCE],
    [1, MAGNIFICATION, 1]
  );

  const scale = useSpring(scaleSync, {
    mass: 0.1,
    stiffness: 150,
    damping: 12,
  });

  const extraMarginSync = useTransform(
    distance,
    [-DISTANCE, 0, DISTANCE],
    [0, MAX_EXTRA_MARGIN, 0]
  );

  const extraMargin = useSpring(extraMarginSync, {
    mass: 0.1,
    stiffness: 150,
    damping: 12,
  });

  return (
    <motion.div
      className="cursor-pointer origin-bottom flex-shrink-0 relative"
      ref={ref}
      style={
        isMobile
          ? {}
          : {
              scale,
              marginLeft: extraMargin,
              marginRight: extraMargin,
            }
      }
      onClick={() => onClick && onClick(badge)}
    >
      {isNew && (
        <>
          {/* Glowing ring around new badge - only on first view */}
          {isFirstView && (
            <motion.div
              animate={{
                opacity: [0.5, 1, 0.5],
                scale: [1, 1.1, 1],
              }}
              className="absolute inset-0 rounded-full bg-gradient-to-r from-yellow-400 via-orange-500 to-pink-500 blur-md -z-10"
              transition={{ duration: 2, repeat: Infinity }}
            />
          )}
          <motion.div
            animate={isFirstView ? { scale: [1, 1.15, 1] } : {}}
            className="absolute -top-2 -right-2 z-10 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-[9px] font-bold px-2 py-1 rounded-full shadow-lg"
            transition={{ duration: 1, repeat: Infinity }}
          >
            NEW
          </motion.div>
        </>
      )}
      <OctagonBadge
        badge={badge}
        index={index}
        isEarned={true}
        showTooltip={false}
        size={isNew && isFirstView ? "lg" : "sm"}
      />
    </motion.div>
  );
};

// Dock container with mouse tracking
const BadgeDock = ({
  badges,
  onBadgeClick,
  newBadgeIds = [],
  firstViewBadgeIds = [],
}) => {
  const mouseX = useMotionValue(Infinity);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return (
    <motion.div
      className="flex items-center sm:justify-center gap-3 sm:gap-4 py-8 scrollbar-hide"
      style={isMobile ? { overflowX: "auto" } : { overflow: "visible" }}
      onMouseLeave={() => mouseX.set(Infinity)}
      onMouseMove={(e) => mouseX.set(e.pageX)}
    >
      {badges.map((badge, index) => (
        <DockBadge
          badge={badge}
          index={index}
          isFirstView={firstViewBadgeIds.includes(badge.id)}
          isMobile={isMobile}
          isNew={newBadgeIds.includes(badge.id)}
          key={badge.id}
          mouseX={mouseX}
          onClick={onBadgeClick}
        />
      ))}
    </motion.div>
  );
};

const Achievements = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [resellerStats, setResellerStats] = useState({
    totalUsers: 0,
    uniqueDomains: 0,
    joinedDate: null,
    tierOrder: 0,
    weeklyGrowth: 0,
    daysWithoutChurn: 0,
  });
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [newBadgeIds, setNewBadgeIds] = useState([]);
  const [firstViewBadgeIds, setFirstViewBadgeIds] = useState([]);

  // Get the most recently acquired badge ID (only one)
  // NEW badge persists until they earn another badge
  // First view shows large + glow, subsequent views show normal size + NEW tag
  useEffect(() => {
    const seenBadges = JSON.parse(
      localStorage.getItem("seenAchievementBadges") || "[]"
    );
    const viewedBadges = JSON.parse(
      localStorage.getItem("viewedAchievementBadges") || "[]"
    );
    const earnedBadges = getEarnedBadges(resellerStats);
    const earnedBadgeIds = earnedBadges.map((b) => b.id);
    const unseenIds = earnedBadgeIds.filter((id) => !seenBadges.includes(id));

    // First visit - mark all as seen, don't show any as NEW
    if (seenBadges.length === 0 && earnedBadgeIds.length > 0) {
      localStorage.setItem(
        "seenAchievementBadges",
        JSON.stringify(earnedBadgeIds)
      );
      localStorage.setItem(
        "viewedAchievementBadges",
        JSON.stringify(earnedBadgeIds)
      );
      setNewBadgeIds([]);
      setFirstViewBadgeIds([]);

      return;
    }

    // Only show NEW on the SINGLE most recent badge (highest order among unseen)
    if (unseenIds.length > 0) {
      const unseenBadges = earnedBadges.filter((b) => unseenIds.includes(b.id));
      const lastAcquired = unseenBadges.sort((a, b) => b.order - a.order)[0];

      if (lastAcquired) {
        setNewBadgeIds([lastAcquired.id]);

        // Check if this is the first time viewing this badge
        if (!viewedBadges.includes(lastAcquired.id)) {
          setFirstViewBadgeIds([lastAcquired.id]);
        } else {
          setFirstViewBadgeIds([]);
        }

        // Mark all OTHER unseen badges as seen (keep only the newest as NEW)
        if (unseenIds.length > 1) {
          const updatedSeen = [
            ...seenBadges,
            ...unseenIds.filter((id) => id !== lastAcquired.id),
          ];

          localStorage.setItem(
            "seenAchievementBadges",
            JSON.stringify(updatedSeen)
          );
        }
      } else {
        setNewBadgeIds([]);
        setFirstViewBadgeIds([]);
      }
    } else {
      setNewBadgeIds([]);
      setFirstViewBadgeIds([]);
    }
  }, [resellerStats, loading]);

  // Mark the NEW badge as viewed (not seen) when leaving the page
  // This removes the glow/size but keeps the NEW tag
  useEffect(
    () => () => {
      if (newBadgeIds.length > 0) {
        const viewedBadges = JSON.parse(
          localStorage.getItem("viewedAchievementBadges") || "[]"
        );
        const updatedViewed = [...new Set([...viewedBadges, ...newBadgeIds])];

        localStorage.setItem(
          "viewedAchievementBadges",
          JSON.stringify(updatedViewed)
        );
      }
    },
    [newBadgeIds]
  );

  // Fetch playlists and reseller stats
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch playlists to calculate stats (same as CustomerDashboard)
        let playlists = [];

        if (user && user.role === "user") {
          const { data: playlistsData } = await apiService.getPlaylists();

          playlists = (playlistsData || []).filter(
            (playlist) => !isReadOnlyPlaylistEntry(playlist)
          );
        }

        // Calculate stats from playlists
        const totalUsers = playlists.length;

        // Get unique domains
        const domains = playlists.map((p) => p.domain);
        const uniqueDomains = [...new Set(domains)].length;

        // Calculate weekly growth (users added in last 7 days)
        const oneWeekAgo = new Date();

        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        const oneWeekAgoStr = oneWeekAgo.toISOString().split("T")[0];
        const weeklyGrowth = playlists.filter((p) => {
          const addedDate = new Date(p.addedDate || p.createdAt)
            .toISOString()
            .split("T")[0];

          return addedDate >= oneWeekAgoStr;
        }).length;

        // Fetch additional stats from API (daysWithoutChurn)
        let daysWithoutChurn = 0;

        try {
          const response = await apiService.get(
            "/v1/dashboard/resellers/stats"
          );

          if (response?.data) {
            daysWithoutChurn = response.data.daysWithoutChurn || 0;
          }
        } catch (error) {
          console.error("Failed to fetch reseller stats:", error);
        }

        setResellerStats({
          totalUsers,
          uniqueDomains,
          joinedDate: user?.joinDate || null,
          tierOrder: user?.resellerTierOrder || 0,
          weeklyGrowth,
          daysWithoutChurn,
        });
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  const earnedBadges = getEarnedBadges(resellerStats);
  const lockedBadges = getLockedBadges(resellerStats);
  // Display badges show only the highest badge in each progression (like dashboard)
  const displayBadges = getDisplayBadges(resellerStats);

  // Filter badges by category
  const filterBadgesByCategory = (badges) => {
    if (selectedCategory === "all") return badges;

    return badges.filter((b) => b.category === selectedCategory);
  };

  const filteredDisplayBadges = filterBadgesByCategory(displayBadges);
  const filteredLockedBadges = filterBadgesByCategory(lockedBadges);

  const handleBadgeClick = (badge) => {
    setSelectedBadge(badge);
    setShowModal(true);
  };

  const categories = [
    { id: "all", label: "All", icon: "fa-th" },
    { id: BADGE_CATEGORIES.USERS, label: "Users", icon: "fa-users" },
    { id: BADGE_CATEGORIES.DOMAINS, label: "Domains", icon: "fa-globe" },
    { id: BADGE_CATEGORIES.LOYALTY, label: "Loyalty", icon: "fa-heart" },
    {
      id: BADGE_CATEGORIES.PERFORMANCE,
      label: "Performance",
      icon: "fa-chart-line",
    },
    { id: BADGE_CATEGORIES.SPECIAL, label: "Special", icon: "fa-star" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500" />
          <span className="text-gray-500 dark:text-gray-400">
            Loading achievements...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-4">
      {/* Header */}
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        className="mb-4"
        initial={{ opacity: 0, y: -20 }}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
            <i className="fas fa-trophy text-white text-lg" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                Achievements
              </h1>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {earnedBadges.length} of {BADGES.length} badges earned
              </span>
            </div>
            {/* Progress bar inline */}
            <div className="flex items-center gap-3 mt-1">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Collection Progress
              </span>
              <div className="flex-1 max-w-xs h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <motion.div
                  animate={{
                    width: `${(earnedBadges.length / BADGES.length) * 100}%`,
                  }}
                  className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
                  initial={{ width: 0 }}
                  transition={{ duration: 1, ease: "easeOut" }}
                />
              </div>
              <span className="text-xs font-semibold text-purple-600 dark:text-purple-400">
                {Math.round((earnedBadges.length / BADGES.length) * 100)}%
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Category Filter */}
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        className="mb-4"
        initial={{ opacity: 0, y: 10 }}
        transition={{ delay: 0.1 }}
      >
        <div className="flex items-center gap-3">
          {/* Categories - horizontal scroll on mobile */}
          <div className="overflow-x-auto scrollbar-hide flex-1 sm:flex-none">
            <div className="flex gap-2 pb-2 sm:pb-0">
              {categories.map((cat) => (
                <button
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 whitespace-nowrap flex-shrink-0 ${
                    selectedCategory === cat.id
                      ? "bg-purple-500 text-white shadow-lg shadow-purple-500/25"
                      : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 shadow-sm"
                  }`}
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                >
                  <i className={`fas ${cat.icon} text-xs`} />
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
          {/* Users count - hidden on mobile, circle on desktop */}
          <div className="hidden sm:block ml-auto">
            <AnimatedUserCount
              count={resellerStats.totalUsers}
              onClick={() => navigate("/dashboard?open=add-playlist")}
            />
          </div>
        </div>
        {/* Users count - full width pill on mobile only */}
        <div className="sm:hidden mt-3">
          <AnimatedUserCount
            count={resellerStats.totalUsers}
            onClick={() => navigate("/dashboard?open=add-playlist")}
          />
        </div>
      </motion.div>

      {/* Earned Badges Section - Dock Style */}
      <div className="mb-8">
        <motion.h2
          animate={{ opacity: 1, y: 0 }}
          className="text-lg font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-4 relative z-0"
          initial={{ opacity: 0, y: 20 }}
          transition={{ delay: 0.2 }}
        >
          <i className="fas fa-check-circle text-green-500" />
          Earned Badges ({filteredDisplayBadges.length})
        </motion.h2>

        {filteredDisplayBadges.length > 0 ? (
          <div className="relative">
            {/* Background card - z-index 1 */}
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="absolute inset-0 bg-gradient-to-br from-gray-200 via-gray-300/80 to-gray-200 dark:from-gray-700 dark:via-gray-800 dark:to-gray-700 rounded-2xl shadow-xl z-[1]"
              initial={{ opacity: 0, y: 20 }}
              transition={{ delay: 0.2 }}
            >
              {/* Background decoration */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-purple-500/5 to-pink-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 hidden sm:block pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-blue-500/5 to-cyan-500/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2 hidden sm:block pointer-events-none" />
            </motion.div>

            {/* Badges */}
            <div className="relative z-[2] px-4 sm:px-6">
              <BadgeDock
                badges={filteredDisplayBadges.sort((a, b) => {
                  // Sort new badges first, then by order
                  const aIsNew = newBadgeIds.includes(a.id);
                  const bIsNew = newBadgeIds.includes(b.id);

                  if (aIsNew && !bIsNew) return -1;
                  if (!aIsNew && bIsNew) return 1;

                  return a.order - b.order;
                })}
                firstViewBadgeIds={firstViewBadgeIds}
                newBadgeIds={newBadgeIds}
                onBadgeClick={handleBadgeClick}
              />
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center">
            <i className="fas fa-medal text-4xl text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-gray-500 dark:text-gray-400">
              No earned badges in this category yet
            </p>
          </div>
        )}
      </div>

      {/* Locked Badges Section */}
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        initial={{ opacity: 0, y: 20 }}
        transition={{ delay: 0.3 }}
      >
        <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
          <i className="fas fa-lock text-gray-400" />
          Locked Badges ({filteredLockedBadges.length})
        </h2>

        {filteredLockedBadges.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {filteredLockedBadges
              .sort((a, b) => a.order - b.order)
              .map((badge, index) => {
                const progress = getBadgeProgress(badge, resellerStats);

                return (
                  <motion.div
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm hover:shadow-lg transition-shadow cursor-pointer flex flex-col items-center relative overflow-hidden"
                    initial={{ opacity: 0, scale: 0.8 }}
                    key={badge.id}
                    transition={{ delay: index * 0.03 }}
                    onClick={() => handleBadgeClick(badge)}
                  >
                    <OctagonBadge
                      badge={badge}
                      index={index}
                      isEarned={false}
                      showTooltip={false}
                      size="sm"
                    />
                    <span className="mt-2 text-xs font-medium text-gray-600 dark:text-gray-400 text-center line-clamp-1">
                      {badge.name}
                    </span>
                    <span className="text-[10px] text-gray-400 dark:text-gray-500 capitalize">
                      {badge.rarity.name}
                    </span>
                    {/* Progress indicator */}
                    {progress > 0 && progress < 100 && (
                      <div className="w-full mt-2">
                        <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <span className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
                          {Math.round(progress)}% complete
                        </span>
                      </div>
                    )}
                  </motion.div>
                );
              })}
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center">
            <i className="fas fa-trophy text-4xl text-green-500 mb-3" />
            <p className="text-gray-500 dark:text-gray-400">
              You've earned all badges in this category!
            </p>
          </div>
        )}
      </motion.div>

      {/* Badge Detail Modal */}
      <BadgeDetailModal
        badge={selectedBadge}
        earnedDate={selectedBadge ? resellerStats.joinedDate : null}
        isEarned={
          selectedBadge
            ? earnedBadges.some((b) => b.id === selectedBadge.id)
            : false
        }
        isOpen={showModal}
        resellerStats={resellerStats}
        onClose={() => {
          setShowModal(false);
          setSelectedBadge(null);
        }}
      />
    </div>
  );
};

export default Achievements;
